/**
 * Book Grid component for displaying books with cover images
 * Displays books in a responsive grid layout similar to Indigo's bestsellers page
 */

import type { TFunction } from 'i18next';
import { Lightbulb, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatMonthYear, formatNumber, formatPrice, formatYear } from '../i18n/format';
import type { BookShelf } from '../services/api';
import type { ThumbsRating } from '../types';
import { getDisplayAuthor } from '../utils/displayAuthor';
import BookShelfMenu from './BookShelfMenu';

export interface BookData {
  book_id?: string;
  isbn?: string;
  sku?: string;
  title?: string;
  author?: string;
  price?: number;
  language?: string;
  publisher?: string;
  themes?: string[];
  genres?: string[];
  theme1?: string;
  theme2?: string;
  THEME1?: string;
  THEME2?: string;
  recommendation_score?: number;
  explanation?: string;
  interaction_type?: string; // 'PURCHASED' or 'BROWSED'
  purchase_date?: string;
  is_canadian_author?: boolean;
  sources?: string[];
  publication_date?: string;
  popularity_score?: number;
  page_count?: number;
  indigo_recommendation_types?: string[];
}

interface BookGridProps {
  books: BookData[];
  showRating?: boolean;
  onRatingChange?: (bookId: string, rating: ThumbsRating) => void;
  ratings?: Record<string, ThumbsRating>;
  title?: string;
  emptyMessage?: string;
  ratingQuestion?: string;
  ratingYesLabel?: string;
  ratingNoLabel?: string;
  showGiftQuestion?: boolean;
  onGiftStatusChange?: (bookId: string, isGift: boolean | null) => void;
  giftStatus?: Record<string, boolean | null>;
  /** False on the customer-facing CDN domains (prices suppressed during beta). */
  showPrices?: boolean;
  /** Hide editorial/merchandising badges. */
  hideBadges?: boolean;
}

const getBookImageUrl = (book: BookData): string => {
  // Use ISBN, SKU, or book_id for the image URL
  const bookIdentifier = book.isbn || book.sku || book.book_id;
  if (!bookIdentifier) {
    return '/placeholder-book.png';
  }
  // Clean the identifier (the endpoint 404s on hyphenated ids)
  const cleanId = bookIdentifier.replace(/-/g, '');
  // Size-controlled endpoint: cards render up to ~200px wide (aspect-[2/3]),
  // so 400x600 stays crisp on retina without shipping the full-size cover.
  return `https://dynamic.indigoimages.ca/v1/books/books/${cleanId}/1.jpg?width=400&maxHeight=600&quality=85`;
};

// Title-case fallback for badge types without an explicit translation.
const titleCaseBadge = (type: string): string =>
  type
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

// Localized badge label; falls back to a title-cased version for unknown types.
const formatBadgeLabel = (type: string, t: TFunction): string =>
  t(`badges.${type}`, { defaultValue: titleCaseBadge(type) });

const isHeatherSlug = (type: string): boolean => type.startsWith('heathers-');

const getBadgeStyle = (type: string): { bg: string; text: string } => {
  if (isHeatherSlug(type)) {
    return { bg: 'bg-purple-100', text: 'text-purple-800' };
  }
  switch (type) {
    case 'BESTSELLER':
      return { bg: 'bg-yellow-100', text: 'text-yellow-800' };
    case 'STAFF_PICK':
      return { bg: 'bg-blue-100', text: 'text-blue-800' };
    case 'BEST_BOOK':
      return { bg: 'bg-green-100', text: 'text-green-800' };
    case 'TRENDING_BOOKTOK':
      return { bg: 'bg-pink-100', text: 'text-pink-800' };
    case 'AWARDED_BOOK':
      return { bg: 'bg-amber-100', text: 'text-amber-800' };
    case 'ANTICIPATED_BOOK':
      return { bg: 'bg-indigo-100', text: 'text-indigo-800' };
    default:
      return { bg: 'bg-gray-100', text: 'text-gray-800' };
  }
};

/**
 * True on touch-primary devices (phones, tablets, and in-app browsers such as
 * Facebook Messenger or the Google app).
 *
 * Detection is by input capability, not screen width: the tap-to-open-details
 * bug is caused by touch scrolling (in-app browsers fire a click at the end of
 * a scroll gesture and don't lock background scroll), so it affects large
 * tablets too while never affecting a narrow desktop window.
 */
const useIsTouchDevice = (): boolean => {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(hover: none) and (pointer: coarse)');
    const update = () => setIsTouch(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return isTouch;
};

const BookCard: React.FC<{
  book: BookData;
  showRating?: boolean;
  rating?: ThumbsRating;
  onRatingChange?: (rating: ThumbsRating) => void;
  ratingQuestion?: string;
  ratingYesLabel?: string;
  ratingNoLabel?: string;
  showGiftQuestion?: boolean;
  onGiftStatusChange?: (isGift: boolean | null) => void;
  isGift?: boolean | null;
  showPrices?: boolean;
  /** Hide Indigo editorial/merchandising tags (Bestseller, Best Book, etc.) */
  hideBadges?: boolean;
  /** Show the shelf picker chip on the cover (For You carousels). */
  showShelfActions?: boolean;
  shelf?: BookShelf | null;
  onShelfChange?: (shelf: BookShelf | null) => void;
  /** Spotlight this card's shelf chip during the onboarding tour. */
  isShelfTourAnchor?: boolean;
  /** Fired on a genuine card tap/click (fires on mobile too, where the modal stays closed). */
  onBookClick?: () => void;
  /**
   * When true the click redirects to the storefront PDP (handled by onBookClick)
   * instead of opening the in-app details modal.
   */
  redirectToPdp?: boolean;
}> = React.memo(
  ({
    book,
    showRating,
    rating,
    onRatingChange,
    ratingQuestion,
    ratingYesLabel,
    ratingNoLabel,
    showGiftQuestion,
    onGiftStatusChange,
    isGift,
    showPrices = true,
    hideBadges = false,
    showShelfActions = false,
    shelf,
    onShelfChange,
    isShelfTourAnchor = false,
    onBookClick,
    redirectToPdp = false,
  }) => {
    const { t } = useTranslation();
    const [imageError, setImageError] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const imageUrl = getBookImageUrl(book);
    const displayAuthor = getDisplayAuthor(book.author, book.publisher);

    // Tap-to-open details is disabled on touch devices: in-app browsers (Facebook
    // Messenger, the Google app, etc.) open the modal on an accidental tap while
    // scrolling and then scroll janks behind the overlay. On desktop (mouse) the
    // interaction stays intact.
    const isTouchDevice = useIsTouchDevice();
    // When redirecting to the storefront PDP the in-app modal is never opened
    // (the click handler navigates to a new tab instead).
    const detailsEnabled = !isTouchDevice && !redirectToPdp;

    // Lock background scroll while the modal is open (desktop only).
    useEffect(() => {
      if (!showDetails) return;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }, [showDetails]);

    // Tap vs. scroll detection so a scroll gesture that ends on the card is not
    // logged as a click (in-app browsers fire a trailing click after scrolling).
    const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
    const didScrollRef = useRef(false);

    const handlePointerDown = (e: React.PointerEvent) => {
      pointerStartRef.current = { x: e.clientX, y: e.clientY };
      didScrollRef.current = false;
    };

    const handlePointerMove = (e: React.PointerEvent) => {
      const start = pointerStartRef.current;
      if (!start) return;
      // 10px tolerance for finger jitter; anything larger is treated as a scroll.
      if (Math.abs(e.clientX - start.x) > 10 || Math.abs(e.clientY - start.y) > 10) {
        didScrollRef.current = true;
      }
    };

    const handlePointerCancel = () => {
      // Fired when the browser takes the gesture over for scrolling.
      didScrollRef.current = true;
    };

    // A genuine tap records the click everywhere; the modal only opens where it
    // is enabled (desktop), so mobile clicks are captured without opening it.
    const handleCardActivate = () => {
      if (didScrollRef.current) {
        didScrollRef.current = false;
        return;
      }
      onBookClick?.();
      if (detailsEnabled) setShowDetails(true);
    };

    const resolvedRatingQuestion = ratingQuestion ?? t('recommendations.wouldYouBuy');
    const resolvedYesLabel = ratingYesLabel ?? t('common.yes');
    const resolvedNoLabel = ratingNoLabel ?? t('common.no');

    // Extract themes from various possible formats
    const themes =
      book.themes || [book.theme1 || book.THEME1, book.theme2 || book.THEME2].filter(Boolean);

    // Memoize indigo recommendation types to prevent re-renders
    const indigoTypes = useMemo(() => {
      const rawTypes = book.indigo_recommendation_types as unknown;
      if (Array.isArray(rawTypes)) return rawTypes;
      if (typeof rawTypes === 'string' && rawTypes.length > 0) {
        return rawTypes
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      return [];
    }, [book.indigo_recommendation_types]);

    return (
      <>
        <div
          className={`bg-white overflow-hidden hover:shadow-sm transition-shadow duration-300 flex flex-col ${
            detailsEnabled || redirectToPdp ? 'cursor-pointer' : ''
          }`}
          onClick={handleCardActivate}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerCancel={handlePointerCancel}
        >
          {/* Book Cover Image */}
          <div className="relative aspect-[2/3] bg-gray-100 overflow-hidden">
            {!imageError ? (
              <img
                src={imageUrl}
                alt={book.title || t('book.coverAlt')}
                className="w-full h-full object-cover"
                onError={() => setImageError(true)}
                loading="lazy"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-indigo-brand-off-white">
                <div className="text-center p-4">
                  <div className="text-4xl mb-2">📚</div>
                  <p className="text-xs text-indigo-brand-text/50 line-clamp-2">{book.title}</p>
                </div>
              </div>
            )}

            {/* Interaction Type Badge */}
            {book.interaction_type && (
              <div
                className={`absolute top-2 left-2 text-white text-xs font-bold px-2 py-1 rounded-full shadow ${
                  book.interaction_type === 'PURCHASED' ? 'bg-green-500' : 'bg-blue-500'
                }`}
              >
                {book.interaction_type === 'PURCHASED'
                  ? `✓ ${t('book.purchased')}`
                  : `✓ ${t('book.browsed')}`}
              </div>
            )}

            {/* Purchase/Browse Date Badge */}
            {book.purchase_date && (
              <div className="absolute top-2 right-2 bg-gray-800/80 text-white text-xs px-2 py-1 rounded-full shadow">
                {formatMonthYear(book.purchase_date)}
              </div>
            )}

            {/* Shelf picker chip + chosen-shelf pill, overlaid on the cover */}
            {showShelfActions && onShelfChange && (
              <BookShelfMenu
                shelf={shelf ?? null}
                onChange={onShelfChange}
                isTourAnchor={isShelfTourAnchor}
              />
            )}
          </div>

          {/* Book Info */}
          <div className="py-3 flex-1 flex flex-col">
            <h3
              className="font-medium tracking-tight text-indigo-brand-text text-sm line-clamp-2 mb-0.5"
              title={book.title}
            >
              {book.title || t('book.unknownTitle')}
            </h3>
            {/* Catalogue titles and authors are both stored uppercase, so the
                author has to stay light or the two lines read as one block. */}
            {displayAuthor && (
              <p className="text-xs tracking-tight text-indigo-brand-text/60 mb-1.5 line-clamp-1">
                {displayAuthor}
              </p>
            )}

            {/* Price (suppressed on the customer-facing CDN domains) */}
            {showPrices && book.price !== undefined && book.price > 0 && (
              <p className="text-sm font-bold text-indigo-brand-black mb-1">
                {formatPrice(book.price)}
              </p>
            )}

            {/* Indigo Recommendation Badges (hidden in CDN mode) */}
            {!hideBadges && indigoTypes.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {indigoTypes.map((type, badgeIdx) => {
                  const badge = getBadgeStyle(type);
                  return (
                    <span
                      key={badgeIdx}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
                    >
                      {formatBadgeLabel(type, t)}
                    </span>
                  );
                })}
              </div>
            )}

            {/* Canadian Author Badge (hidden in CDN mode) */}
            {!hideBadges && book.is_canadian_author && (
              <div className="mb-2">
                <span
                  className="inline-block px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs"
                  title={t('book.canadianAuthor')}
                >
                  {t('book.canadianAuthor')}
                </span>
              </div>
            )}

            {/* Rating Buttons. Presentational wrapper only: the native rating/gift
                buttons below each stopPropagation, so clicks never reach the card's
                open-details handler — no handler is needed on this container. */}
            {showRating && onRatingChange && (
              <div className="mt-auto pt-3 border-t border-gray-100">
                <p className="text-xs text-gray-500 mb-2">{resolvedRatingQuestion}</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRatingChange(rating === 'thumbs_up' ? null : 'thumbs_up');
                    }}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      rating === 'thumbs_up'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600'
                    }`}
                  >
                    <ThumbsUp size={16} />
                    {resolvedYesLabel}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRatingChange(rating === 'thumbs_down' ? null : 'thumbs_down');
                    }}
                    className={`flex-1 flex items-center justify-center gap-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                      rating === 'thumbs_down'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600'
                    }`}
                  >
                    <ThumbsDown size={16} />
                    {resolvedNoLabel}
                  </button>
                </div>

                {/* Gift Question */}
                {showGiftQuestion && onGiftStatusChange && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">{t('history.wasGift')}</p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGiftStatusChange(isGift === true ? null : true);
                        }}
                        className={`flex-1 py-2 px-3 rounded-sm text-sm font-medium transition-colors ${
                          isGift === true
                            ? 'bg-indigo-brand-black text-white'
                            : 'bg-gray-100 text-indigo-brand-text hover:bg-indigo-brand-off-white'
                        }`}
                      >
                        {t('common.yes')}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGiftStatusChange(isGift === false ? null : false);
                        }}
                        className={`flex-1 py-2 px-3 rounded-sm text-sm font-medium transition-colors ${
                          isGift === false
                            ? 'bg-indigo-brand-black text-white'
                            : 'bg-gray-100 text-indigo-brand-text hover:bg-indigo-brand-off-white'
                        }`}
                      >
                        {t('common.no')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Details Modal (disabled on touch devices) */}
        {detailsEnabled && showDetails && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={() => setShowDetails(false)}
          >
            <div
              className="bg-white rounded-sm max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-start justify-between p-4 sm:p-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="w-20 h-30 sm:w-24 sm:h-36 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                    {!imageError ? (
                      <img
                        src={imageUrl}
                        alt={book.title || t('book.coverAlt')}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-indigo-brand-off-white">
                        <span className="text-3xl">📚</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-medium tracking-tight text-gray-800 mb-1">
                      {book.title || t('book.unknownTitle')}
                    </h2>
                    {displayAuthor && (
                      <p className="tracking-tight text-gray-600 mb-2">{displayAuthor}</p>
                    )}
                    {showPrices && book.price !== undefined && book.price > 0 && (
                      <p className="text-lg font-bold text-indigo-brand-black">
                        {formatPrice(book.price)}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowDetails(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X size={24} className="text-gray-500" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-4 sm:p-6 space-y-4">
                {/* Why This Book Is Recommended */}
                {book.explanation && (
                  <div className="bg-indigo-brand-off-white p-4 rounded-sm border-l-4 border-indigo-brand-black">
                    <div className="flex items-start gap-2">
                      <Lightbulb
                        className="text-indigo-brand-black flex-shrink-0 mt-0.5"
                        size={20}
                      />
                      <div>
                        <h3 className="font-semibold text-gray-800 mb-2">
                          {t('book.whyRecommended')}
                        </h3>
                        <p className="text-gray-700 text-sm leading-relaxed">{book.explanation}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Book Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Themes */}
                  {themes.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">{t('book.themes')}</h4>
                      <div className="flex flex-wrap gap-1">
                        {themes.map((theme, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-3 py-1 rounded-sm bg-indigo-brand-off-white text-indigo-brand-text text-sm border border-indigo-brand-border"
                          >
                            {theme}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Genres */}
                  {book.genres && book.genres.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">{t('book.genres')}</h4>
                      <div className="flex flex-wrap gap-1">
                        {book.genres.map((genre, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sources */}
                  {book.sources && book.sources.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-2">
                        {t('book.sources')}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {book.sources.map((source, idx) => (
                          <span
                            key={idx}
                            className="inline-block px-3 py-1 rounded-sm bg-indigo-brand-off-white text-indigo-brand-text text-sm border border-indigo-brand-border"
                          >
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Publisher */}
                  {book.publisher && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {t('book.publisher')}
                      </h4>
                      <p className="text-gray-800">{book.publisher}</p>
                    </div>
                  )}

                  {/* Language */}
                  {book.language && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {t('book.language')}
                      </h4>
                      <p className="text-gray-800">{book.language}</p>
                    </div>
                  )}

                  {/* Canadian Author */}
                  {book.is_canadian_author && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {t('book.canadianAuthor')}
                      </h4>
                      <span className="inline-block px-3 py-1 rounded-full bg-red-100 text-red-700 text-sm font-medium">
                        {t('common.yes')}
                      </span>
                    </div>
                  )}

                  {/* ISBN */}
                  {book.isbn && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">{t('book.isbn')}</h4>
                      <p className="text-gray-800 font-mono text-sm">{book.isbn}</p>
                    </div>
                  )}

                  {/* Published */}
                  {book.publication_date && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {t('book.published')}
                      </h4>
                      <p className="text-gray-800">{formatYear(book.publication_date)}</p>
                    </div>
                  )}

                  {/* Pages */}
                  {book.page_count && book.page_count > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">{t('book.pages')}</h4>
                      <p className="text-gray-800">{book.page_count}</p>
                    </div>
                  )}

                  {/* Popularity */}
                  {book.popularity_score && book.popularity_score > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {t('book.popularity')}
                      </h4>
                      <p className="text-gray-800">{formatNumber(book.popularity_score)}</p>
                    </div>
                  )}

                  {/* Interaction Type */}
                  {book.interaction_type && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {t('book.interaction')}
                      </h4>
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          book.interaction_type === 'PURCHASED'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {book.interaction_type === 'PURCHASED'
                          ? `✓ ${t('book.purchased')}`
                          : `✓ ${t('book.browsed')}`}
                      </span>
                    </div>
                  )}

                  {/* Purchase/Browse Date */}
                  {book.purchase_date && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-600 mb-1">
                        {book.interaction_type === 'PURCHASED'
                          ? t('book.purchaseDate')
                          : t('book.browseDate')}
                      </h4>
                      <span className="text-gray-800">
                        {formatDate(book.purchase_date) || t('common.unknown')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Rating Section in Modal */}
                {showRating && onRatingChange && (
                  <div className="pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-600 mb-3">{resolvedRatingQuestion}</p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => onRatingChange(rating === 'thumbs_up' ? null : 'thumbs_up')}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-sm text-base font-medium transition-colors ${
                          rating === 'thumbs_up'
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-green-100 hover:text-green-600'
                        }`}
                      >
                        <ThumbsUp size={20} />
                        {resolvedYesLabel}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          onRatingChange(rating === 'thumbs_down' ? null : 'thumbs_down')
                        }
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-sm text-base font-medium transition-colors ${
                          rating === 'thumbs_down'
                            ? 'bg-red-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-red-100 hover:text-red-600'
                        }`}
                      >
                        <ThumbsDown size={20} />
                        {resolvedNoLabel}
                      </button>
                    </div>

                    {/* Gift Question in Modal */}
                    {showGiftQuestion && onGiftStatusChange && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-sm text-gray-600 mb-3">{t('history.wasGift')}</p>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => onGiftStatusChange(isGift === true ? null : true)}
                            className={`flex-1 py-3 px-4 rounded-sm text-base font-medium transition-colors ${
                              isGift === true
                                ? 'bg-indigo-brand-black text-white'
                                : 'bg-gray-100 text-indigo-brand-text hover:bg-indigo-brand-off-white'
                            }`}
                          >
                            {t('common.yes')}
                          </button>
                          <button
                            type="button"
                            onClick={() => onGiftStatusChange(isGift === false ? null : false)}
                            className={`flex-1 py-3 px-4 rounded-sm text-base font-medium transition-colors ${
                              isGift === false
                                ? 'bg-indigo-brand-black text-white'
                                : 'bg-gray-100 text-indigo-brand-text hover:bg-indigo-brand-off-white'
                            }`}
                          >
                            {t('common.no')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
);

const BookGrid: React.FC<BookGridProps> = ({
  books,
  showRating = false,
  onRatingChange,
  ratings = {},
  title,
  emptyMessage,
  ratingQuestion,
  ratingYesLabel,
  ratingNoLabel,
  showGiftQuestion,
  onGiftStatusChange,
  giftStatus,
  showPrices = true,
  hideBadges = false,
}) => {
  const { t } = useTranslation();

  if (!books || books.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-5xl mb-4">📚</div>
        <p className="text-gray-500">{emptyMessage ?? t('recommendations.empty')}</p>
      </div>
    );
  }

  const getBookId = (book: BookData, index: number): string => {
    // Use book_id first (which may already include index fallback), then isbn, sku, or generate from index
    return book.book_id || book.isbn || book.sku || `book-${index}`;
  };

  return (
    <div>
      {title && <h3 className="text-xl font-semibold text-gray-800 mb-4">{title}</h3>}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {books.map((book, index) => {
          const bookId = getBookId(book, index);
          return (
            <BookCard
              key={`${bookId}-${index}`}
              book={book}
              showRating={showRating}
              rating={ratings[bookId]}
              onRatingChange={
                onRatingChange ? (rating) => onRatingChange(bookId, rating) : undefined
              }
              ratingQuestion={ratingQuestion}
              ratingYesLabel={ratingYesLabel}
              ratingNoLabel={ratingNoLabel}
              showGiftQuestion={showGiftQuestion}
              onGiftStatusChange={
                onGiftStatusChange ? (isGift) => onGiftStatusChange(bookId, isGift) : undefined
              }
              isGift={giftStatus?.[bookId]}
              showPrices={showPrices}
              hideBadges={hideBadges}
            />
          );
        })}
      </div>
    </div>
  );
};

// Export BookCard for use in profile carousels
export { BookCard };

export default BookGrid;
